import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { Icon } from '@iconify/react';
import { useNavigate } from 'react-router-dom';
import { allCourses } from '../../../services/userService';
import { getActiveCategories } from '../../../services/adminService';
import NoData from '../../../components/common/NoData';
import { encodeData } from '../../../utils/encodeAndEncode';
import CourseCard from './CourseCard';

const SkeletonCard = () => (
  <div className="bg-[var(--bg-primary)] rounded-2xl overflow-hidden border border-[var(--border-color)] animate-pulse">
    <div className="aspect-[16/10] bg-gray-200" />
    <div className="p-5 space-y-3">
      <div className="h-6 bg-gray-200 rounded w-3/4" />
      <div className="h-4 bg-gray-200 rounded w-full" />
      <div className="h-4 bg-gray-200 rounded w-5/6" />
      <div className="flex gap-4 mt-4">
        <div className="h-4 bg-gray-200 rounded w-20" />
        <div className="h-4 bg-gray-200 rounded w-20" />
      </div>
    </div>
  </div>
);

const FEE_RANGES = [
  { id: 'free', label: 'Free', min: null, max: 0 },
  { id: '0-499', label: '₹0 – ₹499', min: 0, max: 499 },
  { id: '500-999', label: '₹500 – ₹999', min: 500, max: 999 },
  { id: '1000-1499', label: '₹1000 – ₹1499', min: 1000, max: 1499 },
  { id: '1500+', label: '₹1500+', min: 1500, max: null },
];

const DURATION_RANGES = [
  { id: 'under1', label: 'Under 1 hour', min: 0, max: 60 },
  { id: '1-3', label: '1–3 hours', min: 60, max: 180 },
  { id: '3-6', label: '3–6 hours', min: 180, max: 360 },
  { id: '6+', label: '6+ hours', min: 360, max: null },
];

function parseDurationToMinutes(durationStr) {
  if (!durationStr || durationStr === '—') return null;
  const s = String(durationStr).toLowerCase();
  let total = 0;
  const hMatch = s.match(/(\d+)\s*h/);
  const mMatch = s.match(/(\d+)\s*m/);
  if (hMatch) total += parseInt(hMatch[1], 10) * 60;
  if (mMatch) total += parseInt(mMatch[1], 10);
  if (total > 0) return total;
  const numMatch = s.match(/(\d+)/);
  return numMatch ? parseInt(numMatch[1], 10) : null;
}

function courseInFeeRange(course, range) {
  if (course.price === 'Free' || course.course_type?.toLowerCase() === 'free') return range.id === 'free';
  const num = parseFloat(String(course.price).replace(/[^0-9.]/g, '')) || 0;
  if (range.min === null && range.max === 0) return false;
  if (range.min === null) return num >= range.max;
  if (range.max === null) return num >= range.min;
  return num >= range.min && num <= range.max;
}

function courseInDurationRange(course, range) {
  const mins = parseDurationToMinutes(course.duration);
  if (mins == null) return false;
  if (range.max === null) return mins >= range.min;
  return mins >= range.min && mins < range.max;
}

function CourseTab({ searchTerm: externalSearchTerm, setIsSearchLoading: externalSetIsSearchLoading }) {
  const navigate = useNavigate();
  const observer = useRef();
  const [courses, setCourses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [categoryMap, setCategoryMap] = useState({});
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [selectedFeeRanges, setSelectedFeeRanges] = useState([]);
  const [selectedDurations, setSelectedDurations] = useState([]);
  const [sortBy, setSortBy] = useState('relevance');
  const [viewMode, setViewMode] = useState('grid');
  const [clickedCourseId, setClickedCourseId] = useState(null);

  // Use external search term if provided
  useEffect(() => {
    if (externalSearchTerm !== undefined) {
      setSearchTerm(externalSearchTerm);
    }
  }, [externalSearchTerm]);

  useEffect(() => {
    getActiveCategories()
      .then((res) => {
        if (res?.success && Array.isArray(res?.data)) {
          const catNames = res.data.map(c => c.category_name);
          const catMap = {};
          res.data.forEach(c => {
            catMap[c.category_name] = c.id;
          });
          setCategories(catNames);
          setCategoryMap(catMap);
        }
      })
      .catch(() => {});
  }, []);

  const fetchCourses = async (pageNum = 1, reset = false, search = '', categoryId = '') => {
    try {
      setIsLoading(true);
      if (externalSetIsSearchLoading) externalSetIsSearchLoading(true);
      const start = Date.now();
      const response = await allCourses({ page: pageNum, limit: 10, search, category: categoryId || undefined });
      const elapsed = Date.now() - start;
      const delay = Math.max(0, 400 - elapsed);

      const process = () => {
        if (response.success && response.data?.courses) {
          let newCourses = response.data.courses.map((course, index) => ({
            id: course.id,
            image: course.banner_image,
            title: course.course_name || 'Untitled Course',
            description: course.course_desc || 'No description provided.',
            modules: response.data.metadata[index]?.totalModules ?? 0,
            enrolled: response.data.metadata[index]?.totalUsers ?? 0,
            duration: response.data.metadata[index]?.duration || '—',
            rating: course.total_rating ?? 0,
            level: course.levels || 'N/A',
            price: course.course_type?.toLowerCase() === 'free' ? 'Free' : (course.course_price || '0.00'),
            course_type: course.course_type,
            currency: course.currency || 'USD',
            course_url: course.course_url || null,
            category: course.course_category || '',
            is_mandatory: course.is_mandatory || 0,
            mandatory_for: course.mandatory_for || 'all',
            instructor_name: course.trainer_name || course.instructor_name || null,
          }));
          setCourses((prev) => (reset ? newCourses : [...prev, ...newCourses]));
          setHasMore(pageNum < response.data.totalPages);
        } else {
          setHasMore(false);
        }
        setIsLoading(false);
        if (externalSetIsSearchLoading) externalSetIsSearchLoading(false);
      };

      if (delay > 0) setTimeout(process, delay);
      else process();
    } catch (err) {
      console.error(err);
      setIsLoading(false);
      if (externalSetIsSearchLoading) externalSetIsSearchLoading(false);
      setHasMore(false);
    }
  };

  const activeCategoryIdForApi = selectedCategories.length > 0 ? categoryMap[selectedCategories[0]] : '';

  useEffect(() => {
    setPage(1);
    fetchCourses(1, true, searchTerm, activeCategoryIdForApi);
  }, [searchTerm, activeCategoryIdForApi]);

  useEffect(() => {
    if (page > 1) fetchCourses(page, false, searchTerm, activeCategoryIdForApi);
  }, [page]);

  const filteredCourses = useMemo(() => {
    let list = [...courses];
    if (selectedCategories.length > 0) {
      const selectedIds = selectedCategories.map(name => categoryMap[name]).filter(Boolean);
      list = list.filter((c) => selectedIds.includes(c.category));
    }
    if (selectedFeeRanges.length > 0) {
      list = list.filter((c) => selectedFeeRanges.some((id) => courseInFeeRange(c, FEE_RANGES.find((r) => r.id === id))));
    }
    if (selectedDurations.length > 0) {
      list = list.filter((c) => selectedDurations.some((id) => courseInDurationRange(c, DURATION_RANGES.find((r) => r.id === id))));
    }
    if (sortBy === 'price-asc') list.sort((a, b) => (parseFloat(String(a.price)) || 0) - (parseFloat(String(b.price)) || 0));
    else if (sortBy === 'price-desc') list.sort((a, b) => (parseFloat(String(b.price)) || 0) - (parseFloat(String(a.price)) || 0));
    else if (sortBy === 'duration') list.sort((a, b) => (parseDurationToMinutes(a.duration) || 0) - (parseDurationToMinutes(b.duration) || 0));
    return list;
  }, [courses, selectedCategories, selectedFeeRanges, selectedDurations, sortBy, categoryMap]);

  const toggleCategory = (cat) => {
    setSelectedCategories((prev) => (prev.includes(cat) ? prev.filter((c) => c !== cat) : [...prev, cat]));
    setPage(1);
  };

  const toggleFeeRange = (id) => {
    setSelectedFeeRanges((prev) => (prev.includes(id) ? prev.filter((r) => r !== id) : [...prev, id]));
  };

  const toggleDuration = (id) => {
    setSelectedDurations((prev) => (prev.includes(id) ? prev.filter((r) => r !== id) : [...prev, id]));
  };

  const clearAllFilters = () => {
    setSelectedCategories([]);
    setSelectedFeeRanges([]);
    setSelectedDurations([]);
  };

  const activeFilterCount = selectedCategories.length + selectedFeeRanges.length + selectedDurations.length;

  const lastCourseRef = useCallback(
    (node) => {
      if (isLoading) return;
      if (observer.current) observer.current.disconnect();
      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasMore) setPage((prev) => prev + 1);
      });
      if (node) observer.current.observe(node);
    },
    [isLoading, hasMore]
  );

  const handleCourseClick = (course) => {
    setClickedCourseId(course.id);
    const encoded = encodeData({ id: course.id });
    setTimeout(() => {
      navigate(`/user/courses/courseDetails/${encodeURIComponent(encoded)}`);
    }, 300);
  };

  return (
    <div className="course-tab-content flex flex-col lg:flex-row gap-6 lg:gap-8">
      {/* Left sidebar - filters */}
      <aside className="lg:w-64 xl:w-72 flex-shrink-0">
        <div className="sticky top-24 rounded-2xl border border-[var(--border-color)] bg-[var(--bg-primary)] p-4 shadow-sm space-y-6">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-[var(--text-primary)] uppercase tracking-wide">Filters</h3>
            {activeFilterCount > 0 && (
              <span className="px-2 py-1 bg-[var(--primary-color)] text-white text-xs rounded-full font-semibold">
                {activeFilterCount}
              </span>
            )}
          </div>

          {/* Fee Range - Hidden */}
          {/* <div>
            <h4 className="text-sm font-medium text-[var(--text-primary)] mb-3">Fee Range</h4>
            <ul className="space-y-2">
              {FEE_RANGES.map((range) => (
                <li key={range.id} className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id={`fee-${range.id}`}
                    checked={selectedFeeRanges.includes(range.id)}
                    onChange={() => toggleFeeRange(range.id)}
                    className="rounded border-[var(--border-color)] text-[var(--primary-color)] focus:ring-[var(--primary-color)]"
                  />
                  <label htmlFor={`fee-${range.id}`} className="text-sm text-[var(--text-secondary)] cursor-pointer select-none">
                    {range.label}
                  </label>
                </li>
              ))}
            </ul>
          </div> */}

          {/* Specialty (Category) */}
          <div>
            <h4 className="text-sm font-medium text-[var(--text-primary)] mb-3">Category</h4>
            <ul className="space-y-2 max-h-64 overflow-y-auto pr-1">
              {categories.map((cat) => (
                <li key={cat} className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id={`cat-${cat.replace(/\s+/g, '-')}`}
                    checked={selectedCategories.includes(cat)}
                    onChange={() => toggleCategory(cat)}
                    className="rounded border-[var(--border-color)] text-[var(--primary-color)] focus:ring-[var(--primary-color)]"
                  />
                  <label htmlFor={`cat-${cat.replace(/\s+/g, '-')}`} className="text-sm text-[var(--text-secondary)] cursor-pointer select-none truncate">
                    {cat}
                  </label>
                </li>
              ))}
            </ul>
          </div>

          {/* Course Duration */}
          <div>
            <h4 className="text-sm font-medium text-[var(--text-primary)] mb-3">Course Duration</h4>
            <ul className="space-y-2">
              {DURATION_RANGES.map((range) => (
                <li key={range.id} className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id={`dur-${range.id}`}
                    checked={selectedDurations.includes(range.id)}
                    onChange={() => toggleDuration(range.id)}
                    className="rounded border-[var(--border-color)] text-[var(--primary-color)] focus:ring-[var(--primary-color)]"
                  />
                  <label htmlFor={`dur-${range.id}`} className="text-sm text-[var(--text-secondary)] cursor-pointer select-none">
                    {range.label}
                  </label>
                </li>
              ))}
            </ul>
          </div>

          {activeFilterCount > 0 && (
            <button
              onClick={clearAllFilters}
              className="w-full px-3 py-2 text-sm font-medium text-red-600 hover:bg-red-50 rounded-lg transition-colors"
            >
              Clear all filters
            </button>
          )}
        </div>
      </aside>

      {/* Main content */}
      <main className="flex-1 min-w-0">
        {activeFilterCount > 0 && (
          <div className="flex flex-wrap items-center gap-2 mb-4 p-3 bg-[var(--bg-secondary)] rounded-lg border border-[var(--border-color)]">
            <span className="text-sm text-[var(--text-secondary)] font-medium">Active filters:</span>
            {selectedCategories.map(cat => (
              <button
                key={cat}
                onClick={() => toggleCategory(cat)}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-[var(--primary-color)]/10 text-[var(--primary-color)] rounded-full text-sm font-medium hover:bg-[var(--primary-color)]/20 transition-colors"
              >
                {cat}
                <Icon icon="mdi:close" width={14} />
              </button>
            ))}
            {selectedFeeRanges.map(id => {
              const range = FEE_RANGES.find(r => r.id === id);
              return (
                <button
                  key={id}
                  onClick={() => toggleFeeRange(id)}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-[var(--primary-color)]/10 text-[var(--primary-color)] rounded-full text-sm font-medium hover:bg-[var(--primary-color)]/20 transition-colors"
                >
                  {range?.label}
                  <Icon icon="mdi:close" width={14} />
                </button>
              );
            })}
            {selectedDurations.map(id => {
              const range = DURATION_RANGES.find(r => r.id === id);
              return (
                <button
                  key={id}
                  onClick={() => toggleDuration(id)}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-[var(--primary-color)]/10 text-[var(--primary-color)] rounded-full text-sm font-medium hover:bg-[var(--primary-color)]/20 transition-colors"
                >
                  {range?.label}
                  <Icon icon="mdi:close" width={14} />
                </button>
              );
            })}
            <button
              onClick={clearAllFilters}
              className="ml-auto text-sm text-red-600 hover:underline font-medium"
            >
              Clear all
            </button>
          </div>
        )}

        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div className="flex items-center gap-3 flex-shrink-0">
            <div className="flex items-center gap-2">
              <span className="text-sm text-[var(--text-secondary)]">Sort by</span>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="rounded-xl border-2 border-[var(--border-color)] bg-[var(--bg-primary)] px-3 py-2 text-sm text-[var(--text-primary)] focus:outline-none focus:ring-2 focus:ring-[var(--primary-color)]/30 focus:border-[var(--primary-color)]"
                aria-label="Sort courses"
              >
                <option value="relevance">Relevance</option>
                <option value="price-asc">Price: Low to High</option>
                <option value="price-desc">Price: High to Low</option>
                <option value="duration">Duration</option>
              </select>
            </div>
            <div className="flex rounded-lg border-2 border-[var(--border-color)] overflow-hidden">
              <button
                type="button"
                onClick={() => setViewMode('grid')}
                className={`p-2 ${viewMode === 'grid' ? 'bg-[var(--primary-color)] text-white' : 'bg-[var(--bg-primary)] text-[var(--text-secondary)]'}`}
                aria-label="Grid view"
              >
                <Icon icon="mdi:view-grid-outline" width={20} height={20} />
              </button>
              <button
                type="button"
                onClick={() => setViewMode('list')}
                className={`p-2 ${viewMode === 'list' ? 'bg-[var(--primary-color)] text-white' : 'bg-[var(--bg-primary)] text-[var(--text-secondary)]'}`}
                aria-label="List view"
              >
                <Icon icon="mdi:view-list-outline" width={20} height={20} />
              </button>
            </div>
          </div>
        </div>

        {filteredCourses.length === 0 && !isLoading && (
          <div className="flex justify-center items-center w-full min-h-[340px] rounded-2xl bg-[var(--bg-secondary)] border border-[var(--border-color)]">
            <NoData message="No courses match your filters. Try adjusting search or filters." />
          </div>
        )}

        {isLoading && courses.length === 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5 sm:gap-6">
            {[...Array(6)].map((_, i) => <SkeletonCard key={i} />)}
          </div>
        ) : (
          <div
            className={
              viewMode === 'list'
                ? 'grid grid-cols-1 gap-5'
                : 'grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5 sm:gap-6'
            }
          >
            {filteredCourses.map((course, index) => (
              <CourseCard
                key={course.id}
                course={course}
                mode="browse"
                viewMode={viewMode}
                onAction={handleCourseClick}
                isActionLoading={clickedCourseId === course.id}
                lastRef={index === filteredCourses.length - 1 ? lastCourseRef : null}
              />
            ))}
          </div>
        )}

        {isLoading && courses.length > 0 && (
          <div className="flex justify-center py-6">
            <span className="w-8 h-8 border-2 border-[var(--primary-color)] border-t-transparent rounded-full animate-spin" role="status" />
          </div>
        )}
      </main>
    </div>
  );
}

export default CourseTab;
